import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { 
  Search, 
  Package, 
  ArrowRightLeft, 
  TrendingUp, 
  TrendingDown,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Loader2,
  RefreshCw,
  Send,
  BarChart3,
  PieChart,
  ArrowUpRight,
  ArrowDownRight,
  Minus,
  Download,
  FileSpreadsheet,
  Settings,
  Save
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const BRANCHES = {
  QC: { name: "Quezon City", color: "#3B82F6" },
  PQ: { name: "Parañaque", color: "#8B5CF6" },
  CV: { name: "Cavite", color: "#F59E0B" },
  PM: { name: "Pampanga", color: "#EC4899" },
  VZ: { name: "Valenzuela", color: "#10B981" },
  "OTG-A": { name: "Orion On The Go - A", color: "#06B6D4" },
  "OTG-B": { name: "Orion On The Go - B", color: "#0EA5E9" },
  "OTG-C": { name: "Orion On The Go - C", color: "#6366F1" }
};

const BRANCH_CODES = Object.keys(BRANCHES);

// Status styles using exact spec hex codes
// Severity priority order (most urgent → least urgent): CRITICAL → ORDER NOW → PARTIAL COVERAGE → MONITOR → HEALTHY → NO SALES DATA → NOT STOCKED
const STATUS_STYLES = {
  CRITICAL: { bg: "bg-[#FEE2E2]", text: "text-[#DC2626]", border: "border-[#FCA5A5]" },
  "ORDER NOW": { bg: "bg-[#FFEDD5]", text: "text-[#EA580C]", border: "border-[#FDBA74]" },
  "PARTIAL COVERAGE": { bg: "bg-[#FEF9C3]", text: "text-[#CA8A04]", border: "border-[#FDE68A]" },
  MONITOR: { bg: "bg-[#DBEAFE]", text: "text-[#2563EB]", border: "border-[#93C5FD]" },
  HEALTHY: { bg: "bg-[#DCFCE7]", text: "text-[#16A34A]", border: "border-[#86EFAC]" },
  "LOW DEMAND": { bg: "bg-[#F1F5F9]", text: "text-[#64748B]", border: "border-[#CBD5E1]" },
  "NO SALES DATA": { bg: "bg-[#F3F4F6]", text: "text-[#6B7280]", border: "border-[#D1D5DB]" },
  "NOT STOCKED": { bg: "bg-[#E5E7EB]", text: "text-[#374151]", border: "border-[#9CA3AF]" },
  // Back-compat for any legacy status that may still appear
  OUT: { bg: "bg-[#FEE2E2]", text: "text-[#DC2626]", border: "border-[#FCA5A5]" },
  LOW: { bg: "bg-[#FEF9C3]", text: "text-[#CA8A04]", border: "border-[#FDE68A]" },
  OK: { bg: "bg-[#DCFCE7]", text: "text-[#16A34A]", border: "border-[#86EFAC]" },
  HIGH: { bg: "bg-[#DBEAFE]", text: "text-[#2563EB]", border: "border-[#93C5FD]" },
  "NO MOVEMENT": { bg: "bg-[#F3F4F6]", text: "text-[#6B7280]", border: "border-[#D1D5DB]" },
  "BRANCH STOCKOUT": { bg: "bg-[#FEF9C3]", text: "text-[#CA8A04]", border: "border-[#FDE68A]" },
};

const STATUS_SEVERITY = {
  CRITICAL: 0,
  "ORDER NOW": 1,
  "PARTIAL COVERAGE": 2,
  "BRANCH STOCKOUT": 2,
  OUT: 0,
  LOW: 2,
  MONITOR: 3,
  HEALTHY: 4,
  OK: 4,
  HIGH: 4,
  "NO SALES DATA": 5,
  "LOW DEMAND": 5,
  "NO MOVEMENT": 5,
  "NOT STOCKED": 6,
};

export default function GlobalInventoryMatrix() {
  const { user: authUser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [adoBreakdown, setAdoBreakdown] = useState(null);
  const [adoBreakdownLoading, setAdoBreakdownLoading] = useState(false);
  const [cleanupOpen, setCleanupOpen] = useState(false);
  const [cleanupData, setCleanupData] = useState(null);
  const [cleanupLoading, setCleanupLoading] = useState(false);
  const [cleanupApplying, setCleanupApplying] = useState(false);
  const [merging, setMerging] = useState(false);

  const runMergeDuplicates = async () => {
    if (!window.confirm("I-standardize ang mga item names na nagkakaiba lang sa capitalization/spacing?\n\nHal: 'HVN White H4' + 'HVN WHITE H4' → iisang pangalan na lang.\n\nSafe ito — pagsasamahin ang records at stocks, walang mabubura na data.")) return;
    setMerging(true);
    try {
      const resp = await axios.post(`${API}/admin/merge-duplicate-item-names`, {});
      const d = resp.data;
      if (d.merged_groups === 0) {
        toast.success("✅ Walang duplicate item names — malinis na!");
      } else {
        toast.success(`✅ ${d.merged_groups} item name group(s) na-merge! Hal: ${d.details[0]?.merged_variants?.join(", ")} → ${d.details[0]?.canonical}`);
        fetchMatrixData();
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Merge failed");
    } finally {
      setMerging(false);
    }
  };

  const openRecountCleanup = async () => {
    setCleanupOpen(true);
    setCleanupLoading(true);
    setCleanupData(null);
    try {
      const resp = await axios.get(`${API}/admin/recount-sales-cleanup/preview`);
      setCleanupData(resp.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Hindi makuha ang cleanup preview");
      setCleanupOpen(false);
    } finally {
      setCleanupLoading(false);
    }
  };

  const applyRecountCleanup = async () => {
    if (!cleanupData?.entries?.length) return;
    if (!window.confirm(`I-reclassify ang ${cleanupData.entries.length} entries bilang RECOUNT_ADJUSTMENT? Matatanggal sila sa ADO/sales computation (hindi magbabago ang stock).`)) return;
    setCleanupApplying(true);
    try {
      const resp = await axios.post(`${API}/admin/recount-sales-cleanup/apply`, {
        entries: cleanupData.entries.map((e) => ({ collection: e.collection, id: e.id })),
      });
      toast.success(`✅ ${resp.data.reclassified} entries na-reclassify. Na-update na ang ADO.`);
      setCleanupOpen(false);
      fetchMatrixData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Cleanup apply failed");
    } finally {
      setCleanupApplying(false);
    }
  };

  const openAdoBreakdown = async (itemName) => {
    setAdoBreakdownLoading(true);
    setAdoBreakdown({ item_name: itemName, sources: [] });
    try {
      const resp = await axios.get(`${API}/analytics/ado-breakdown`, {
        params: { item_name: itemName, days: velocityDays }
      });
      setAdoBreakdown(resp.data);
    } catch (e) {
      toast.error("Hindi makuha ang ADO breakdown");
      setAdoBreakdown(null);
    } finally {
      setAdoBreakdownLoading(false);
    }
  };
  const [matrixData, setMatrixData] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [showZero, setShowZero] = useState(false);
  const [statusFilter, setStatusFilter] = useState("all");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [activeTab, setActiveTab] = useState("matrix");
  const [velocityDays, setVelocityDays] = useState(30); // 30, 60, or 90 days for ADO/DTL calculation
  
  // --- Supplier ordering (Forecasting tab) ---
  // When `orderFilterOn` is true, the Forecasting table shows ONLY items that
  // need to be ordered (status OUT / ORDER NOW / FOLLOW UP!, optionally MONITOR
  // too). A "Suggested Order Qty" column appears, computed from
  //   max(0, ceil(ADO × targetDaysCover − current_stock − incoming))
  const [orderFilterOn, setOrderFilterOn] = useState(false);
  const [orderIncludeMonitor, setOrderIncludeMonitor] = useState(false);
  const [targetDaysCover, setTargetDaysCover] = useState(60);
  const [supplierName, setSupplierName] = useState("");
  
  // Quick action modals (from summary cards)
  const [showOrderAllModal, setShowOrderAllModal] = useState(false);
  const [showTransferModal, setShowTransferModal] = useState(false);
  
  // Request modal state
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [requestQty, setRequestQty] = useState(1);
  const [requestSource, setRequestSource] = useState("");
  const [requestDest, setRequestDest] = useState("");
  const [requestNotes, setRequestNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  
  // User's branch and admin status
  const [userBranch, setUserBranch] = useState("QC");
  const [isAdmin, setIsAdmin] = useState(false);

  // Forecasting settings — Minimum ADO for Auto Restock (configurable)
  const [minAdoSetting, setMinAdoSetting] = useState(0.5);
  const [minAdoInput, setMinAdoInput] = useState("0.5");
  const [savingMinAdo, setSavingMinAdo] = useState(false);
  const [showSettings, setShowSettings] = useState(false);

  const fetchForecastingSettings = async () => {
    try {
      const token = localStorage.getItem("auth_token");
      const res = await axios.get(`${API}/system-settings/forecasting`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMinAdoSetting(res.data.min_ado_for_restock);
      setMinAdoInput(String(res.data.min_ado_for_restock));
    } catch (e) {
      console.error("Error fetching forecasting settings:", e);
    }
  };

  const saveMinAdoSetting = async () => {
    const val = parseFloat(minAdoInput);
    if (isNaN(val) || val < 0 || val > 100) {
      toast.error("Minimum ADO ay dapat sa pagitan ng 0 at 100");
      return;
    }
    setSavingMinAdo(true);
    try {
      const token = localStorage.getItem("auth_token");
      const res = await axios.put(`${API}/system-settings/forecasting`, { min_ado_for_restock: val }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(res.data.message);
      setMinAdoSetting(val);
      setShowSettings(false);
      fetchMatrixData(); // re-fetch so statuses recompute with new threshold
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save setting");
    } finally {
      setSavingMinAdo(false);
    }
  };

  useEffect(() => {
    if (authUser) {
      const adminStatus = authUser.can_access_all_branches || authUser.is_admin || authUser.role === "admin";
      setIsAdmin(adminStatus);
      
      // For admins, don't set a default branch so all branches appear in Request FROM
      if (adminStatus) {
        setUserBranch(""); // Empty means no branch is filtered out
        setRequestDest("QC"); // Default destination for admins
      } else {
        const branchesList = authUser.assigned_branches?.length ? authUser.assigned_branches : (authUser.assigned_branch ? [authUser.assigned_branch] : []);
        setUserBranch(branchesList[0] || "QC");
        setRequestDest(branchesList[0] || "QC");
      }
    }
  }, [authUser]);

  useEffect(() => {
    fetchMatrixData();
  }, [showZero, velocityDays]); // Re-fetch when velocity days changes

  useEffect(() => {
    fetchForecastingSettings();
  }, []);

  const fetchMatrixData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("auth_token");
      const response = await axios.get(`${API}/global-inventory-matrix`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { show_zero: showZero, days: velocityDays }
      });
      setMatrixData(response.data);
    } catch (error) {
      console.error("Error fetching matrix:", error);
      toast.error("Failed to load inventory matrix");
    } finally {
      setLoading(false);
    }
  };

  // Get unique categories from data
  const categories = useMemo(() => {
    if (!matrixData?.matrix) return [];
    const cats = new Set();
    matrixData.matrix.forEach(item => {
      // Extract category from item name (e.g., "Alpha White H4" -> "Alpha")
      const firstWord = item.item_name.split(' ')[0];
      cats.add(firstWord);
    });
    return Array.from(cats).sort();
  }, [matrixData]);

  // Filter and enhance items
  const filteredItems = useMemo(() => {
    if (!matrixData?.matrix) return [];
    
    return matrixData.matrix
      .filter(item => {
        const matchesSearch = !searchTerm || 
          item.item_name.toLowerCase().includes(searchTerm.toLowerCase());
        
        // Handle stock status filters (OUT, CRITICAL, LOW, OK, HIGH)
        let matchesStatus = statusFilter === "all" || item.status === statusFilter;
        
        // Handle movement filters
        const ado = item.ado || 0;
        if (statusFilter === "FAST_MOVING") matchesStatus = ado >= 5;
        else if (statusFilter === "GOOD_MOVING") matchesStatus = ado >= 2 && ado < 5;
        else if (statusFilter === "SLOW_MOVING") matchesStatus = ado >= 0.5 && ado < 2;
        else if (statusFilter === "VERY_SLOW") matchesStatus = ado > 0 && ado < 0.5;
        else if (statusFilter === "NO_MOVEMENT") matchesStatus = ado === 0 || ado === null;
        
        const matchesCategory = categoryFilter === "all" || 
          item.item_name.toLowerCase().startsWith(categoryFilter.toLowerCase());
        return matchesSearch && matchesStatus && matchesCategory;
      })
      .map(item => {
        // Calculate distribution percentages
        const total = item.total_stock || 1;
        const distribution = {};
        BRANCH_CODES.forEach(branch => {
          distribution[branch] = total > 0 
            ? Math.round((item.branches[branch] / total) * 100) 
            : 0;
        });
        
        // Find branches with stock to request from
        // For admins: show ALL branches with stock (they can distribute to any branch)
        // For regular users: filter out their own branch (can only request FROM other branches)
        const sourceBranches = BRANCH_CODES
          .filter(b => item.branches[b] > 0)
          .sort((a, b) => (item.branches[b] || 0) - (item.branches[a] || 0));
        
        // Find best source (branch with highest stock)
        const best_source_branch = sourceBranches.length > 0 ? sourceBranches[0] : null;
        const best_source_qty = best_source_branch ? item.branches[best_source_branch] : 0;
        
        return {
          ...item,
          distribution,
          sourceBranches,
          best_source_branch,
          best_source_qty,
          needsRestock: userBranch ? (item.branches[userBranch] < 10 && sourceBranches.length > 0) : false
        };
      })
      .sort((a, b) => {
        // Default sort: severity (CRITICAL first) → blended ADO desc (high velocity first within same status)
        const rankA = STATUS_SEVERITY[a.status] ?? 99;
        const rankB = STATUS_SEVERITY[b.status] ?? 99;
        if (rankA !== rankB) return rankA - rankB;
        const adoA = a.blended_ado ?? a.ado ?? 0;
        const adoB = b.blended_ado ?? b.ado ?? 0;
        return adoB - adoA;
      });
  }, [matrixData, searchTerm, statusFilter, categoryFilter, userBranch, isAdmin]);

  // Compute summary from matrix data
  const summary = useMemo(() => {
    if (!matrixData?.matrix) return { out_of_stock: 0, critical_stock: 0, low_stock: 0, ok_stock: 0, high_stock: 0, no_movement: 0 };
    
    const counts = {
      critical: 0, order_now: 0, partial_coverage: 0, monitor: 0, healthy: 0, no_sales_data: 0, low_demand: 0, no_movement: 0,
    };
    matrixData.matrix.forEach(item => {
      const status = item.status || '';
      if (status === 'CRITICAL') counts.critical++;
      else if (status === 'ORDER NOW') counts.order_now++;
      else if (status === 'PARTIAL COVERAGE') counts.partial_coverage++;
      else if (status === 'MONITOR') counts.monitor++;
      else if (status === 'HEALTHY') counts.healthy++;
      else if (status === 'NO SALES DATA') counts.no_sales_data++;
      else if (status === 'LOW DEMAND') counts.low_demand++;
      // Movement counter (uses ADO)
      if (item.ado === 0 || item.ado === null) counts.no_movement++;
    });
    return counts;
  }, [matrixData]);

  // Aggregate stats by category
  const categoryStats = useMemo(() => {
    if (!filteredItems.length) return [];
    
    const stats = {};
    filteredItems.forEach(item => {
      const category = item.item_name.split(' ')[0];
      if (!stats[category]) {
        stats[category] = { 
          category, 
          totalStock: 0, 
          items: 0,
          branches: { QC: 0, PQ: 0, CV: 0, PM: 0, VZ: 0 }
        };
      }
      stats[category].totalStock += item.total_stock;
      stats[category].items++;
      BRANCH_CODES.forEach(b => {
        stats[category].branches[b] += item.branches[b];
      });
    });
    
    return Object.values(stats).sort((a, b) => b.totalStock - a.totalStock);
  }, [filteredItems]);

  // -------------------------------------------------------------------------
  // Supplier Ordering — derive a clean list of items that NEED to be ordered.
  // The same status logic used in the Forecasting table is recomputed here so
  // the filter is in sync with what users see. Suggested order qty defaults to
  // (ADO × targetDaysCover − total_stock − incoming), floored at 0.
  // -------------------------------------------------------------------------
  const itemsToOrder = useMemo(() => {
    if (!filteredItems.length) return [];

    const minAdo = matrixData?.min_ado_for_restock ?? 0.5;
    const CRITICAL_STATUSES = new Set(["OUT", "ORDER NOW", "FOLLOW UP!", "CRITICAL", "BRANCH STOCKOUT"]);
    const MONITOR_STATUSES = new Set(["MONITOR", "INCOMING"]);

    return filteredItems
      .map(item => {
        const ado = item.ado || 0;
        const dtl = item.dtl;
        const incomingStock = item.incoming_stock || 0;
        const incomingStatus = item.incoming_status;

        // Mirror status logic from the table render
        let status = "OK";
        if (item.total_stock === 0) {
          status = incomingStock > 0 ? "FOLLOW UP!" : "OUT";
        } else if (typeof dtl === "number" && dtl <= 30) {
          if (incomingStock > 0 && incomingStatus === "LATE") status = "FOLLOW UP!";
          else if (incomingStock > 0) status = "INCOMING";
          else status = "ORDER NOW";
        } else if (typeof dtl === "number" && dtl <= 60) {
          status = incomingStock > 0 ? "INCOMING" : "MONITOR";
        } else if (typeof dtl === "number" && dtl <= 90) {
          status = "WATCH";
        }

        const targetQty = Math.ceil(ado * targetDaysCover);
        const suggestedQty = Math.max(0, targetQty - (item.total_stock || 0) - incomingStock);

        return { ...item, computed_status: status, suggested_qty: suggestedQty };
      })
      .filter(item => {
        // LOW DEMAND FILTER: exclude items below Minimum ADO threshold from restock recommendations
        const itemAdo = item.blended_ado ?? item.ado ?? 0;
        if (item.low_demand || item.status === "LOW DEMAND" || itemAdo < minAdo) return false;
        if (CRITICAL_STATUSES.has(item.computed_status)) return true;
        if (orderIncludeMonitor && MONITOR_STATUSES.has(item.computed_status)) return true;
        return false;
      })
      // Sort by urgency: OUT first, then ORDER NOW, then FOLLOW UP!, then the rest
      .sort((a, b) => {
        const rank = { "OUT": 0, "ORDER NOW": 1, "FOLLOW UP!": 2, "MONITOR": 3, "INCOMING": 4 };
        const ra = rank[a.computed_status] ?? 99;
        const rb = rank[b.computed_status] ?? 99;
        if (ra !== rb) return ra - rb;
        return (b.ado || 0) - (a.ado || 0);
      });
  }, [filteredItems, orderIncludeMonitor, targetDaysCover]);

  // The list rendered in the Forecasting tab — full list, or order-only subset.
  const forecastingRows = orderFilterOn ? itemsToOrder : filteredItems;

  // -------------------- Send-to-Supplier actions --------------------
  const buildOrderText = () => {
    const today = new Date().toLocaleDateString("en-PH", { year: "numeric", month: "short", day: "numeric" });
    const header = supplierName
      ? `PURCHASE ORDER REQUEST\nTo: ${supplierName}\nDate: ${today}\n${"=".repeat(40)}\n`
      : `PURCHASE ORDER REQUEST\nDate: ${today}\n${"=".repeat(40)}\n`;
    const lines = itemsToOrder.map((it, i) => {
      const qty = it.suggested_qty;
      return `${(i + 1).toString().padStart(2, " ")}. ${it.item_name}` +
             `\n     Order qty: ${qty}   (current ${it.total_stock || 0}, ADO ${it.ado || 0}/day, DTL ${it.dtl ?? "-"}d)`;
    });
    return `${header}${lines.join("\n")}\n${"=".repeat(40)}\nTotal SKUs: ${itemsToOrder.length}`;
  };

  const copyOrderList = async () => {
    if (!itemsToOrder.length) { toast.error("Walang items na need i-order"); return; }
    try {
      await navigator.clipboard.writeText(buildOrderText());
      toast.success(`Copied ${itemsToOrder.length} items to clipboard`);
    } catch {
      toast.error("Hindi makapag-copy — paki-allow clipboard access");
    }
  };

  const exportOrderToExcel = () => {
    if (!itemsToOrder.length) { toast.error("Walang items na need i-order"); return; }
    const today = new Date().toLocaleDateString("en-PH", { year: "numeric", month: "short", day: "numeric" });
    let html = `<html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:x="urn:schemas-microsoft-com:office:excel">
<head><meta charset="UTF-8"><style>
  table { border-collapse: collapse; }
  th { background:#4F46E5; color:white; font-weight:bold; padding:8px; border:1px solid #ddd; }
  td { padding:8px; border:1px solid #ddd; }
  .num { text-align:right; }
  .out { background:#FEE2E2; } .order { background:#FED7AA; } .follow { background:#FEF3C7; }
  .monitor { background:#DBEAFE; }
</style></head><body>
<h2>Purchase Order Request${supplierName ? ` — ${supplierName}` : ""}</h2>
<div>Date: ${today} | Target days cover: ${targetDaysCover} | SKUs: ${itemsToOrder.length}</div>
<table>
  <tr>
    <th>#</th><th>Item Name</th><th>Order Qty</th><th>Current Stock</th>
    <th>Incoming</th><th>ADO/day</th><th>DTL (days)</th><th>Status</th>
  </tr>`;
    itemsToOrder.forEach((it, i) => {
      const cls = it.computed_status === "OUT" ? "out"
                : it.computed_status === "ORDER NOW" ? "order"
                : it.computed_status === "FOLLOW UP!" ? "follow"
                : "monitor";
      html += `<tr class="${cls}">
        <td>${i + 1}</td>
        <td>${it.item_name}</td>
        <td class="num"><strong>${it.suggested_qty}</strong></td>
        <td class="num">${it.total_stock || 0}</td>
        <td class="num">${it.incoming_stock || 0}</td>
        <td class="num">${it.ado || 0}</td>
        <td class="num">${it.dtl ?? "-"}</td>
        <td>${it.computed_status}</td>
      </tr>`;
    });
    html += `</table></body></html>`;
    const blob = new Blob([html], { type: "application/vnd.ms-excel" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `Purchase_Order_${new Date().toISOString().split("T")[0]}.xls`;
    document.body.appendChild(link); link.click(); document.body.removeChild(link);
    toast.success(`Exported ${itemsToOrder.length} items to Excel`);
  };

  const printOrderSheet = () => {
    if (!itemsToOrder.length) { toast.error("Walang items na need i-order"); return; }
    const today = new Date().toLocaleDateString("en-PH", { year: "numeric", month: "short", day: "numeric" });
    const rows = itemsToOrder.map((it, i) => `
      <tr>
        <td>${i + 1}</td>
        <td>${it.item_name}</td>
        <td class="num"><strong>${it.suggested_qty}</strong></td>
        <td class="num">${it.total_stock || 0}</td>
        <td class="num">${it.incoming_stock || 0}</td>
        <td class="num">${it.ado || 0}</td>
        <td class="num">${it.dtl ?? "-"}</td>
        <td>${it.computed_status}</td>
      </tr>`).join("");
    const html = `<!doctype html><html><head><title>Purchase Order — ${today}</title>
<style>
  body { font-family: Arial, sans-serif; padding: 24px; color:#111; }
  h1 { margin: 0 0 4px; font-size: 20px; }
  .meta { font-size: 12px; color:#444; margin-bottom: 14px; }
  table { border-collapse: collapse; width: 100%; font-size: 12px; }
  th { background:#111; color:#fff; padding: 8px 6px; text-align: left; }
  td { padding: 7px 6px; border-bottom: 1px solid #ddd; }
  .num { text-align: right; }
  .footer { margin-top: 30px; font-size: 11px; color:#666; }
  .sig { margin-top: 60px; display:flex; gap:40px; }
  .sig div { flex: 1; border-top: 1px solid #000; padding-top: 6px; font-size: 11px; }
  @media print { body { padding: 12px; } }
</style></head><body>
  <h1>PURCHASE ORDER REQUEST</h1>
  <div class="meta">
    ${supplierName ? `<strong>To:</strong> ${supplierName} &nbsp;|&nbsp; ` : ""}
    <strong>Date:</strong> ${today} &nbsp;|&nbsp;
    <strong>Target Days Cover:</strong> ${targetDaysCover} &nbsp;|&nbsp;
    <strong>Total SKUs:</strong> ${itemsToOrder.length}
  </div>
  <table>
    <thead><tr>
      <th>#</th><th>Item</th><th class="num">Order Qty</th><th class="num">On Hand</th>
      <th class="num">Incoming</th><th class="num">ADO/d</th><th class="num">DTL (d)</th><th>Status</th>
    </tr></thead>
    <tbody>${rows}</tbody>
  </table>
  <div class="sig">
    <div>Prepared by</div>
    <div>Approved by</div>
    <div>Supplier ack.</div>
  </div>
  <div class="footer">Auto-generated from Orion Global Inventory Matrix · Forecasting</div>
  <script>window.onload=()=>{setTimeout(()=>window.print(),200);};</script>
</body></html>`;
    const w = window.open("", "_blank", "width=900,height=700");
    if (!w) { toast.error("Pop-up blocked — paki-enable pop-ups for printing"); return; }
    w.document.open(); w.document.write(html); w.document.close();
  };

  // Open request modal
  const openRequestModal = (item) => {
    setSelectedItem(item);
    
    // Find branch with highest stock
    const branchesWithStock = BRANCH_CODES.filter(b => item.branches[b] > 0);
    const sortedBranches = branchesWithStock.sort((a, b) => (item.branches[b] || 0) - (item.branches[a] || 0));
    const highestStockBranch = sortedBranches[0] || "";
    
    setRequestSource(highestStockBranch); // Default to branch with most stock
    setRequestDest(userBranch || "QC"); // Ensure destination is set
    setRequestQty(1);
    setRequestNotes("");
    setShowRequestModal(true);
  };

  // Submit request — now with explicit confirmation step to prevent qty-mistakes
  const submitRequest = async () => {
    if (!selectedItem || !requestSource || !requestDest || requestQty <= 0) {
      toast.error("Please fill all required fields");
      return;
    }
    
    if (requestSource === requestDest) {
      toast.error("Source and destination cannot be the same");
      return;
    }
    
    const numQty = parseFloat(requestQty);
    const hasFraction = numQty % 1 !== 0;
    
    // CONFIRMATION DIALOG — surface the EXACT qty being sent so the user can catch typos
    const confirmMsg = hasFraction
      ? `Sigurado ka ba na ${numQty} units ang i-request?\n\n⚠️ MAY FRACTION ITO (${numQty}). Kung gusto mo ng whole number, i-cancel at i-edit ang quantity.\n\nItem: ${selectedItem.item_name}\nFrom: ${requestSource} → To: ${requestDest}`
      : `Sigurado ka ba na ${numQty} units ang i-request?\n\nItem: ${selectedItem.item_name}\nFrom: ${requestSource} → To: ${requestDest}`;
    
    if (!window.confirm(confirmMsg)) {
      return;
    }
    
    setSubmitting(true);
    try {
      const token = localStorage.getItem("auth_token");
      const response = await axios.post(`${API}/item-requests`, {
        item_name: selectedItem.item_name,
        quantity: numQty,
        source_branch: requestSource,
        destination_branch: requestDest,
        notes: requestNotes || ""
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(`✅ Request sent to ${requestSource}! Qty: ${numQty} | Request ID: ${response.data?.request_id || 'Created'}`);
      
      // Reset state and close modal
      setSelectedItem(null);
      setRequestSource("");
      setRequestQty(1);
      setRequestNotes("");
      setShowRequestModal(false);
      
      // Refresh data
      fetchMatrixData();
    } catch (error) {
      console.error("Request error:", error);
      const errorMsg = error.response?.data?.detail || error.message || "Failed to create request";
      toast.error(`❌ ${errorMsg}`);
    } finally {
      setSubmitting(false);
    }
  };

  // Export to CSV function
  const exportToCSV = () => {
    if (!filteredItems.length) {
      toast.error("No data to export");
      return;
    }

    // Create CSV header
    let csv = "Item Name,QC,PQ,CV,PM,VZ,TOTAL,ADO,DTL,Status,Distribution QC %,Distribution PQ %,Distribution CV %,Distribution PM %,Distribution VZ %\n";
    
    // Add data rows
    filteredItems.forEach(item => {
      const row = [
        `"${item.item_name.replace(/"/g, '""')}"`,
        item.branches.QC || 0,
        item.branches.PQ || 0,
        item.branches.CV || 0,
        item.branches.PM || 0,
        item.branches.VZ || 0,
        item.total_stock || 0,
        item.ado || 0,
        item.dtl || "N/A",
        item.status || "UNKNOWN",
        `${item.distribution?.QC || 0}%`,
        `${item.distribution?.PQ || 0}%`,
        `${item.distribution?.CV || 0}%`,
        `${item.distribution?.PM || 0}%`,
        `${item.distribution?.VZ || 0}%`
      ];
      csv += row.join(",") + "\n";
    });

    // Download CSV
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", `Global_Matrix_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    toast.success(`✅ Exported ${filteredItems.length} items to CSV`);
  };

  // Export to Excel (XLSX format via HTML table)
  const exportToExcel = () => {
    if (!filteredItems.length) {
      toast.error("No data to export");
      return;
    }

    // Create HTML table for Excel
    let html = `
      <html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:x="urn:schemas-microsoft-com:office:excel">
      <head><meta charset="UTF-8">
      <style>
        table { border-collapse: collapse; }
        th { background-color: #4F46E5; color: white; font-weight: bold; padding: 8px; border: 1px solid #ddd; }
        td { padding: 8px; border: 1px solid #ddd; }
        .number { text-align: right; }
        .critical { background-color: #FEE2E2; }
        .low { background-color: #FEF3C7; }
        .ok { background-color: #D1FAE5; }
        .no-movement { background-color: #F3F4F6; }
      </style>
      </head>
      <body>
      <table>
        <tr>
          <th>Item Name</th>
          <th>QC</th>
          <th>PQ</th>
          <th>CV</th>
          <th>PM</th>
          <th>VZ</th>
          <th>TOTAL</th>
          <th>ADO</th>
          <th>DTL</th>
          <th>Status</th>
          <th>QC %</th>
          <th>PQ %</th>
          <th>CV %</th>
          <th>PM %</th>
          <th>VZ %</th>
        </tr>`;
    
    filteredItems.forEach(item => {
      const statusClass = item.status === "CRITICAL" ? "critical" : 
                          item.status === "LOW" ? "low" : 
                          item.status === "OK" ? "ok" : "no-movement";
      html += `
        <tr class="${statusClass}">
          <td>${item.item_name}</td>
          <td class="number">${item.branches.QC || 0}</td>
          <td class="number">${item.branches.PQ || 0}</td>
          <td class="number">${item.branches.CV || 0}</td>
          <td class="number">${item.branches.PM || 0}</td>
          <td class="number">${item.branches.VZ || 0}</td>
          <td class="number"><strong>${item.total_stock || 0}</strong></td>
          <td class="number">${item.ado || 0}</td>
          <td class="number">${item.dtl || "N/A"}</td>
          <td>${item.status || "UNKNOWN"}</td>
          <td class="number">${item.distribution?.QC || 0}%</td>
          <td class="number">${item.distribution?.PQ || 0}%</td>
          <td class="number">${item.distribution?.CV || 0}%</td>
          <td class="number">${item.distribution?.PM || 0}%</td>
          <td class="number">${item.distribution?.VZ || 0}%</td>
        </tr>`;
    });
    
    html += `</table></body></html>`;

    // Download as .xls (Excel compatible HTML)
    const blob = new Blob([html], { type: "application/vnd.ms-excel" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", `Global_Matrix_${new Date().toISOString().split('T')[0]}.xls`);
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    toast.success(`✅ Exported ${filteredItems.length} items to Excel`);
  };

  // Get cell styling
  const getCellStyle = (qty, isUserBranch) => {
    let base = "text-center font-medium transition-colors ";
    if (qty <= 0) return base + "bg-gray-50 text-gray-400";
    if (qty < 5) return base + "bg-red-50 text-red-700";
    if (qty < 20) return base + "bg-yellow-50 text-yellow-700";
    if (qty < 50) return base + "bg-green-50 text-green-700";
    return base + "bg-blue-50 text-blue-700";
  };

  // Distribution bar component
  const DistributionBar = ({ item }) => (
    <div className="flex h-2 rounded-full overflow-hidden bg-gray-100">
      {BRANCH_CODES.map(branch => (
        <div
          key={branch}
          style={{ 
            width: `${item.distribution[branch]}%`,
            backgroundColor: BRANCHES[branch].color
          }}
          title={`${branch}: ${item.distribution[branch]}%`}
        />
      ))}
    </div>
  );

  // Order All: consolidated purchase suggestion for CRITICAL + ORDER NOW items
  const orderAllSuggestions = matrixData ? (() => {
    const items = matrixData.matrix.filter(
      (it) => !it.low_demand && (it.status === "CRITICAL" || it.status === "ORDER NOW")
    );
    const target = 60; // target 60d coverage after restock
    return items.map((it) => {
      const ado = it.blended_ado ?? it.ado ?? 0;
      const stock = it.active_total_stock ?? it.total_stock ?? 0;
      const incoming = it.incoming ?? 0;
      const suggested = Math.max(0, Math.ceil(ado * target - stock - incoming));
      return {
        item_name: it.item_name,
        status: it.status,
        current_stock: stock,
        incoming,
        ado: Number((ado || 0).toFixed(2)),
        dtl: it.dtl,
        suggested_qty: suggested,
        zero_channels: (it.zero_channels || []).join(", "),
      };
    }).sort((a, b) => b.suggested_qty - a.suggested_qty);
  })() : [];
  
  // Transfer Suggestions: for PARTIAL COVERAGE items, suggest rebalance from high-stock branch
  const transferSuggestions = matrixData ? (() => {
    const items = matrixData.matrix.filter((it) => it.status === "PARTIAL COVERAGE");
    const suggestions = [];
    items.forEach((it) => {
      const branches = it.branches || {};
      const zero_channels = it.zero_channels || [];
      const active = ["QC", "PQ", "CV", "PM", "VZ", "OTG-A", "OTG-B"];
      const sources = active
        .filter((b) => !zero_channels.includes(b))
        .map((b) => ({ branch: b, stock: branches[b] || 0 }))
        .filter((s) => s.stock > 10)
        .sort((a, b) => b.stock - a.stock);
      if (sources.length === 0) return;
      const source = sources[0];
      const per_dest_qty = Math.max(1, Math.floor(source.stock * 0.1));
      zero_channels.forEach((dest) => {
        suggestions.push({
          item_name: it.item_name,
          from_branch: source.branch,
          from_stock: source.stock,
          to_branch: dest,
          suggested_qty: per_dest_qty,
        });
      });
    });
    return suggestions;
  })() : [];

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text)
      .then(() => toast.success(`✅ ${label} copied to clipboard`))
      .catch(() => toast.error("Copy failed"));
  };

  const downloadCSV = (rows, filename) => {
    if (!rows.length) {
      toast.error("Wala pang data");
      return;
    }
    const headers = Object.keys(rows[0]);
    const csv = [
      headers.join(","),
      ...rows.map((r) => headers.map((h) => `"${String(r[h] ?? "").replace(/"/g, '""')}"`).join(",")),
    ].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
    toast.success(`📥 ${filename} downloaded`);
  };

  return (
    <div className="p-4 md:p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <BarChart3 className="h-7 w-7 text-indigo-600" />
            Global Inventory Matrix
          </h1>
          <p className="text-gray-500">Item-centric view across all branches • Distribution analysis</p>
        </div>
        
        <div className="flex gap-2 items-center">
          {/* Velocity Selector */}
          <div className="flex items-center gap-1 bg-slate-100 rounded-lg p-1">
            {[30, 60, 90].map(d => (
              <Button
                key={d}
                variant={velocityDays === d ? "default" : "ghost"}
                size="sm"
                className={`h-8 px-3 text-xs font-medium ${
                  velocityDays === d ? 'bg-indigo-600 text-white hover:bg-indigo-700' : ''
                }`}
                onClick={() => setVelocityDays(d)}
              >
                {d} Days
              </Button>
            ))}
          </div>
          <Button onClick={fetchMatrixData} variant="outline" size="sm" className="gap-1">
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          {authUser?.role === "admin" && (
            <Button
              onClick={runMergeDuplicates}
              variant="outline"
              size="sm"
              disabled={merging}
              className="gap-1 bg-amber-50 hover:bg-amber-100 text-amber-700 border-amber-200"
              data-testid="merge-duplicates-btn"
              title="I-standardize ang item names na magkaiba lang ang capitalization"
            >
              {merging ? "Merging..." : "Merge Duplicates"}
            </Button>
          )}
          {authUser?.role === "admin" && (
            <Button
              onClick={openRecountCleanup}
              variant="outline"
              size="sm"
              className="gap-1 bg-rose-50 hover:bg-rose-100 text-rose-700 border-rose-200"
              data-testid="recount-cleanup-btn"
              title="Hanapin ang mga recount/adjustment entries na napasok sa sales flow — for approval bago tanggalin sa ADO"
            >
              Recount Cleanup
            </Button>
          )}
          {/* Export Button */}
          <Button 
            onClick={() => exportToCSV()} 
            variant="outline" 
            size="sm" 
            className="gap-1 bg-green-50 hover:bg-green-100 text-green-700 border-green-200"
          >
            <Download className="h-4 w-4" />
            Export CSV
          </Button>
          <Button 
            onClick={() => exportToExcel()} 
            variant="outline" 
            size="sm" 
            className="gap-1 bg-blue-50 hover:bg-blue-100 text-blue-700 border-blue-200"
          >
            <FileSpreadsheet className="h-4 w-4" />
            Export Excel
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      {matrixData && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          {BRANCH_CODES.map(branch => {
            const branchTotal = filteredItems.reduce((sum, item) => sum + (item.branches[branch] || 0), 0);
            const branchItems = filteredItems.filter(item => item.branches[branch] > 0).length;
            return (
              <Card key={branch} className="border-l-4" style={{ borderLeftColor: BRANCHES[branch].color }}>
                <CardContent className="p-3">
                  <div className="flex items-center justify-between">
                    <span className="font-bold text-lg">{branch}</span>
                    <span className="text-xs text-gray-500">{BRANCHES[branch].name}</span>
                  </div>
                  <div className="mt-1">
                    <span className="text-2xl font-bold">{branchTotal.toLocaleString()}</span>
                    <span className="text-xs text-gray-500 ml-1">units</span>
                  </div>
                  <div className="text-xs text-gray-500">{branchItems} items in stock</div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Status Summary — v2 categories mapped to actual item statuses */}
      {matrixData && (
        <div className="grid grid-cols-6 gap-3">
          <Card className={`${STATUS_STYLES.CRITICAL.bg} ${STATUS_STYLES.CRITICAL.border} border cursor-pointer hover:shadow-md transition-shadow ${statusFilter === "CRITICAL" ? "ring-2 ring-red-400" : ""}`}
            onClick={() => setStatusFilter(statusFilter === "CRITICAL" ? "all" : "CRITICAL")}
            data-testid="filter-critical">
            <CardContent className="p-3 flex items-center gap-2">
              <XCircle className="h-5 w-5 text-[#DC2626]" />
              <div className="flex-1">
                <p className="text-xs text-[#DC2626] font-semibold">Critical</p>
                <p className="text-xl font-bold text-[#DC2626]">{summary.critical || 0}</p>
              </div>
              {(summary.critical > 0 || summary.order_now > 0) && (
                <button
                  onClick={(e) => { e.stopPropagation(); setShowOrderAllModal(true); }}
                  className="text-[10px] px-2 py-1 rounded bg-[#DC2626] text-white hover:bg-[#B91C1C] font-semibold whitespace-nowrap"
                  data-testid="order-all-btn"
                  title="Generate consolidated purchase order for all critical + order-now items"
                >
                  Order All →
                </button>
              )}
            </CardContent>
          </Card>
          
          <Card className={`${STATUS_STYLES["ORDER NOW"].bg} ${STATUS_STYLES["ORDER NOW"].border} border cursor-pointer hover:shadow-md transition-shadow ${statusFilter === "ORDER NOW" ? "ring-2 ring-orange-400" : ""}`}
            onClick={() => setStatusFilter(statusFilter === "ORDER NOW" ? "all" : "ORDER NOW")}
            data-testid="filter-order-now">
            <CardContent className="p-3 flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-[#EA580C]" />
              <div>
                <p className="text-xs text-[#EA580C] font-semibold">Order Now</p>
                <p className="text-xl font-bold text-[#EA580C]">{summary.order_now || 0}</p>
              </div>
            </CardContent>
          </Card>
          
          <Card className={`${STATUS_STYLES["PARTIAL COVERAGE"].bg} ${STATUS_STYLES["PARTIAL COVERAGE"].border} border cursor-pointer hover:shadow-md transition-shadow ${statusFilter === "PARTIAL COVERAGE" ? "ring-2 ring-yellow-400" : ""}`}
            onClick={() => setStatusFilter(statusFilter === "PARTIAL COVERAGE" ? "all" : "PARTIAL COVERAGE")}
            data-testid="filter-partial-coverage">
            <CardContent className="p-3 flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-[#CA8A04]" />
              <div className="flex-1">
                <p className="text-xs text-[#CA8A04] font-semibold">Partial Coverage</p>
                <p className="text-xl font-bold text-[#CA8A04]">{summary.partial_coverage || 0}</p>
              </div>
              {summary.partial_coverage > 0 && (
                <button
                  onClick={(e) => { e.stopPropagation(); setShowTransferModal(true); }}
                  className="text-[10px] px-2 py-1 rounded bg-[#CA8A04] text-white hover:bg-[#A16207] font-semibold whitespace-nowrap"
                  data-testid="suggest-transfers-btn"
                  title="Auto-compute transfer suggestions to rebalance partial coverage items"
                >
                  Rebalance →
                </button>
              )}
            </CardContent>
          </Card>
          
          <Card className={`${STATUS_STYLES.MONITOR.bg} ${STATUS_STYLES.MONITOR.border} border cursor-pointer hover:shadow-md transition-shadow ${statusFilter === "MONITOR" ? "ring-2 ring-blue-400" : ""}`}
            onClick={() => setStatusFilter(statusFilter === "MONITOR" ? "all" : "MONITOR")}
            data-testid="filter-monitor">
            <CardContent className="p-3 flex items-center gap-2">
              <TrendingUp className="h-5 w-5 text-[#2563EB]" />
              <div>
                <p className="text-xs text-[#2563EB] font-semibold">Monitor</p>
                <p className="text-xl font-bold text-[#2563EB]">{summary.monitor || 0}</p>
              </div>
            </CardContent>
          </Card>
          
          <Card className={`${STATUS_STYLES.HEALTHY.bg} ${STATUS_STYLES.HEALTHY.border} border cursor-pointer hover:shadow-md transition-shadow ${statusFilter === "HEALTHY" ? "ring-2 ring-green-400" : ""}`}
            onClick={() => setStatusFilter(statusFilter === "HEALTHY" ? "all" : "HEALTHY")}
            data-testid="filter-healthy">
            <CardContent className="p-3 flex items-center gap-2">
              <CheckCircle className="h-5 w-5 text-[#16A34A]" />
              <div>
                <p className="text-xs text-[#16A34A] font-semibold">Healthy</p>
                <p className="text-xl font-bold text-[#16A34A]">{summary.healthy || 0}</p>
              </div>
            </CardContent>
          </Card>

          <Card className={`${STATUS_STYLES["NO SALES DATA"].bg} ${STATUS_STYLES["NO SALES DATA"].border} border cursor-pointer hover:shadow-md transition-shadow ${statusFilter === "NO SALES DATA" ? "ring-2 ring-gray-400" : ""}`}
            onClick={() => setStatusFilter(statusFilter === "NO SALES DATA" ? "all" : "NO SALES DATA")}
            data-testid="filter-no-sales-data">
            <CardContent className="p-3 flex items-center gap-2">
              <XCircle className="h-5 w-5 text-[#6B7280]" />
              <div>
                <p className="text-xs text-[#6B7280] font-semibold">No Sales Data</p>
                <p className="text-xl font-bold text-[#6B7280]">{summary.no_sales_data || 0}</p>
              </div>
            </CardContent>
          </Card>

          <Card className={`${STATUS_STYLES["LOW DEMAND"].bg} ${STATUS_STYLES["LOW DEMAND"].border} border cursor-pointer hover:shadow-md transition-shadow ${statusFilter === "LOW DEMAND" ? "ring-2 ring-slate-400" : ""}`}
            onClick={() => setStatusFilter(statusFilter === "LOW DEMAND" ? "all" : "LOW DEMAND")}
            data-testid="filter-low-demand">
            <CardContent className="p-3 flex items-center gap-2">
              <TrendingDown className="h-5 w-5 text-[#64748B]" />
              <div>
                <p className="text-xs text-[#64748B] font-semibold">Low Demand</p>
                <p className="text-xl font-bold text-[#64748B]">{summary.low_demand || 0}</p>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Movement Filter - Fast/Slow/No Movement */}
      {matrixData && (
        <div className="flex flex-wrap gap-2 items-center">
          <span className="text-sm font-medium text-gray-600">Movement:</span>
          <Badge 
            variant={statusFilter === "FAST_MOVING" ? "default" : "outline"}
            className="cursor-pointer hover:bg-green-100"
            onClick={() => setStatusFilter(statusFilter === "FAST_MOVING" ? "all" : "FAST_MOVING")}
          >
            🚀 Fast Moving ({filteredItems.filter(i => (i.ado || 0) >= 5).length})
          </Badge>
          <Badge 
            variant={statusFilter === "GOOD_MOVING" ? "default" : "outline"}
            className="cursor-pointer hover:bg-blue-100"
            onClick={() => setStatusFilter(statusFilter === "GOOD_MOVING" ? "all" : "GOOD_MOVING")}
          >
            📈 Good ({filteredItems.filter(i => (i.ado || 0) >= 2 && (i.ado || 0) < 5).length})
          </Badge>
          <Badge 
            variant={statusFilter === "SLOW_MOVING" ? "default" : "outline"}
            className="cursor-pointer hover:bg-yellow-100"
            onClick={() => setStatusFilter(statusFilter === "SLOW_MOVING" ? "all" : "SLOW_MOVING")}
          >
            🐢 Slow Moving ({filteredItems.filter(i => (i.ado || 0) >= 0.5 && (i.ado || 0) < 2).length})
          </Badge>
          <Badge 
            variant={statusFilter === "VERY_SLOW" ? "default" : "outline"}
            className="cursor-pointer hover:bg-orange-100"
            onClick={() => setStatusFilter(statusFilter === "VERY_SLOW" ? "all" : "VERY_SLOW")}
          >
            ⚠️ Very Slow ({filteredItems.filter(i => (i.ado || 0) > 0 && (i.ado || 0) < 0.5).length})
          </Badge>
          <Badge 
            variant={statusFilter === "NO_MOVEMENT" ? "default" : "outline"}
            className="cursor-pointer hover:bg-gray-100"
            onClick={() => setStatusFilter(statusFilter === "NO_MOVEMENT" ? "all" : "NO_MOVEMENT")}
          >
            ❌ No Movement ({summary.no_movement})
          </Badge>
          {statusFilter !== "all" && statusFilter.includes("_") && (
            <Button variant="ghost" size="sm" onClick={() => setStatusFilter("all")} className="text-xs">
              Clear Filter
            </Button>
          )}
        </div>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="matrix">📊 Item Matrix</TabsTrigger>
          <TabsTrigger value="forecasting">📅 Forecasting</TabsTrigger>
          <TabsTrigger value="distribution">📈 Distribution Analysis</TabsTrigger>
          <TabsTrigger value="restock">🔄 Restock Suggestions</TabsTrigger>
        </TabsList>

        {/* Forecasting Tab - Excel-style view */}
        <TabsContent value="forecasting" className="space-y-4">
          {/* Supplier Order Filter Toolbar */}
          <Card data-testid="supplier-order-toolbar">
            <CardContent className="p-4">
              <div className="flex flex-col lg:flex-row lg:items-end gap-3">
                <div className="flex items-center gap-2">
                  <Button
                    variant={orderFilterOn ? "default" : "outline"}
                    onClick={() => setOrderFilterOn(v => !v)}
                    className={orderFilterOn ? "bg-orange-600 hover:bg-orange-700 text-white" : ""}
                    data-testid="toggle-order-filter-btn"
                  >
                    <AlertTriangle className="h-4 w-4 mr-2" />
                    {orderFilterOn ? `Showing ${itemsToOrder.length} to order` : "Show items to order"}
                  </Button>
                  {orderFilterOn && (
                    <label className="flex items-center gap-2 text-sm text-gray-700 ml-2">
                      <input
                        type="checkbox"
                        checked={orderIncludeMonitor}
                        onChange={(e) => setOrderIncludeMonitor(e.target.checked)}
                        data-testid="include-monitor-checkbox"
                      />
                      Include MONITOR
                    </label>
                  )}
                  {/* Minimum ADO threshold indicator + settings */}
                  <div className="flex items-center gap-1 ml-2 px-2 py-1 bg-slate-100 rounded-md border border-slate-200" data-testid="min-ado-indicator">
                    <TrendingDown className="h-3.5 w-3.5 text-slate-500" />
                    <span className="text-xs text-slate-600">Min ADO: <strong>{minAdoSetting}/day</strong></span>
                    {isAdmin && (
                      <Button variant="ghost" size="sm" className="h-6 w-6 p-0 ml-1" onClick={() => { setShowSettings(v => !v); setMinAdoInput(String(minAdoSetting)); }} data-testid="min-ado-settings-btn" title="Configure Minimum ADO">
                        <Settings className="h-3.5 w-3.5 text-slate-500" />
                      </Button>
                    )}
                  </div>
                </div>

                {showSettings && isAdmin && (
                  <div className="w-full lg:w-auto p-3 bg-slate-50 border border-slate-300 rounded-lg" data-testid="min-ado-settings-panel">
                    <label className="text-xs font-semibold text-slate-700 block mb-1">
                      Minimum ADO for Auto Restock (/day)
                    </label>
                    <p className="text-[11px] text-slate-500 mb-2 max-w-md">
                      Ang mga items na mas mababa dito ang ADO ay markahan bilang <strong>LOW DEMAND</strong> at hindi isasama sa Restock Suggestions. Makikita pa rin sila sa Item Matrix.
                    </p>
                    <div className="flex items-center gap-2">
                      <Input
                        type="number"
                        step="0.05"
                        min="0"
                        max="100"
                        value={minAdoInput}
                        onChange={(e) => setMinAdoInput(e.target.value)}
                        className="w-28 h-8"
                        data-testid="min-ado-input"
                      />
                      <span className="text-xs text-slate-500">/day</span>
                      <Button size="sm" onClick={saveMinAdoSetting} disabled={savingMinAdo} className="bg-slate-700 hover:bg-slate-800" data-testid="save-min-ado-btn">
                        {savingMinAdo ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><Save className="h-3.5 w-3.5 mr-1" />Save</>}
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => setShowSettings(false)}>Cancel</Button>
                    </div>
                  </div>
                )}

                {orderFilterOn && (
                  <>
                    <div className="flex items-center gap-2">
                      <label className="text-sm text-gray-700 whitespace-nowrap">Target days cover:</label>
                      <Input
                        type="number"
                        min="1"
                        max="365"
                        value={targetDaysCover}
                        onChange={(e) => setTargetDaysCover(Math.max(1, parseInt(e.target.value) || 60))}
                        className="w-20"
                        data-testid="target-days-cover-input"
                      />
                    </div>
                    <div className="flex items-center gap-2">
                      <label className="text-sm text-gray-700 whitespace-nowrap">Supplier:</label>
                      <Input
                        type="text"
                        placeholder="(optional)"
                        value={supplierName}
                        onChange={(e) => setSupplierName(e.target.value)}
                        className="w-44"
                        data-testid="supplier-name-input"
                      />
                    </div>

                    <div className="flex flex-wrap items-center gap-2 ml-auto">
                      <Button
                        onClick={copyOrderList}
                        size="sm"
                        variant="outline"
                        disabled={!itemsToOrder.length}
                        data-testid="copy-order-list-btn"
                      >
                        <Send className="h-4 w-4 mr-1" /> Copy
                      </Button>
                      <Button
                        onClick={exportOrderToExcel}
                        size="sm"
                        variant="outline"
                        disabled={!itemsToOrder.length}
                        data-testid="export-order-excel-btn"
                      >
                        <FileSpreadsheet className="h-4 w-4 mr-1" /> Excel
                      </Button>
                      <Button
                        onClick={printOrderSheet}
                        size="sm"
                        className="bg-indigo-600 hover:bg-indigo-700 text-white"
                        disabled={!itemsToOrder.length}
                        data-testid="print-order-sheet-btn"
                      >
                        <Download className="h-4 w-4 mr-1" /> Print PO
                      </Button>
                    </div>
                  </>
                )}
              </div>

              {orderFilterOn && (
                <p className="text-xs text-gray-500 mt-3">
                  Order qty = max(0, ADO × {targetDaysCover} days − current stock − incoming).
                  Filter shows items with status <strong>OUT</strong>, <strong>ORDER NOW</strong>, <strong>FOLLOW UP!</strong>
                  {orderIncludeMonitor && ", and MONITOR"}.
                </p>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2">
                <BarChart3 className="h-5 w-5 text-indigo-600" />
                Stock Forecasting Matrix
                <span className="ml-auto text-sm font-normal text-gray-500">
                  Based on <span className="font-semibold text-indigo-600">{velocityDays}-day</span> sales velocity
                </span>
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-800 text-white">
                    <tr>
                      <th className="text-center px-2 py-3 font-semibold w-12">NO</th>
                      <th className="text-left px-3 py-3 font-semibold sticky left-0 bg-slate-800 z-10">ITEMS</th>
                      <th className="text-center px-3 py-3 font-semibold">TOTAL STOCKS</th>
                      <th className="text-center px-3 py-3 font-semibold bg-blue-700">ADO</th>
                      <th className="text-center px-3 py-3 font-semibold bg-purple-700">MOVEMENT</th>
                      <th className="text-center px-3 py-3 font-semibold bg-cyan-700">INCOMING</th>
                      <th className="text-center px-3 py-3 font-semibold bg-amber-600">DTL (projected)</th>
                      <th className="text-center px-3 py-3 font-semibold bg-red-700">OS DATE (projected)</th>
                      <th className="text-center px-3 py-3 font-semibold bg-pink-700">STATUS</th>
                      {orderFilterOn && (
                        <th className="text-center px-3 py-3 font-semibold bg-orange-600">ORDER QTY</th>
                      )}
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {forecastingRows.length === 0 && orderFilterOn && (
                      <tr>
                        <td colSpan={10} className="text-center py-10 text-gray-500" data-testid="empty-order-list">
                          🎉 Walang items na need i-order ngayon — healthy ang stock levels mo!
                        </td>
                      </tr>
                    )}
                    {forecastingRows.map((item, idx) => {
                      // Calculate movement classification
                      const ado = item.ado || 0;
                      let movement = "NO MOVEMENT";
                      let movementColor = "bg-gray-100 text-gray-600";
                      if (ado >= 5) { movement = "FAST MOVING"; movementColor = "bg-green-100 text-green-700"; }
                      else if (ado >= 2) { movement = "GOOD"; movementColor = "bg-blue-100 text-blue-700"; }
                      else if (ado >= 0.5) { movement = "SLOW MOVING"; movementColor = "bg-yellow-100 text-yellow-700"; }
                      else if (ado > 0) { movement = "VERY SLOW"; movementColor = "bg-orange-100 text-orange-700"; }
                      
                      // Calculate DTL and OS Date
                      const dtl = item.dtl;
                      const osDate = item.projected_out_date;
                      
                      // Incoming stock info
                      const incomingStock = item.incoming_stock || 0;
                      const incomingArrival = item.incoming_arrival || "";
                      const incomingStatus = item.incoming_status;
                      const incomingWarning = item.incoming_warning;
                      
                      // Status color based on DTL
                      let rowBg = "";
                      let dtlColor = "text-green-600";
                      if (item.total_stock === 0) {
                        rowBg = "bg-red-50";
                        dtlColor = "text-red-600 font-bold";
                      } else if (typeof dtl === 'number' && dtl <= 30) {
                        rowBg = "bg-red-50";
                        dtlColor = "text-red-600 font-bold";
                      } else if (typeof dtl === 'number' && dtl <= 60) {
                        rowBg = "bg-amber-50";
                        dtlColor = "text-amber-600 font-bold";
                      } else if (typeof dtl === 'number' && dtl <= 90) {
                        rowBg = "bg-yellow-50";
                        dtlColor = "text-yellow-600";
                      }
                      
                      // Determine status based on DTL and incoming
                      let statusLabel = "OK";
                      let statusColor = "bg-green-100 text-green-700";
                      if (item.total_stock === 0) {
                        statusLabel = incomingStock > 0 ? "FOLLOW UP!" : "OUT";
                        statusColor = "bg-red-100 text-red-700";
                      } else if (typeof dtl === 'number' && dtl <= 30) {
                        if (incomingStock > 0 && incomingStatus === "LATE") {
                          statusLabel = "FOLLOW UP!";
                          statusColor = "bg-orange-100 text-orange-700";
                        } else if (incomingStock > 0) {
                          statusLabel = "INCOMING";
                          statusColor = "bg-cyan-100 text-cyan-700";
                        } else {
                          statusLabel = "ORDER NOW";
                          statusColor = "bg-red-100 text-red-700";
                        }
                      } else if (typeof dtl === 'number' && dtl <= 60) {
                        statusLabel = incomingStock > 0 ? "INCOMING" : "MONITOR";
                        statusColor = incomingStock > 0 ? "bg-cyan-100 text-cyan-700" : "bg-amber-100 text-amber-700";
                      } else if (typeof dtl === 'number' && dtl <= 90) {
                        statusLabel = "WATCH";
                        statusColor = "bg-yellow-100 text-yellow-700";
                      }
                      
                      return (
                        <tr key={idx} className={`hover:bg-slate-50 ${rowBg}`} title={incomingWarning || ""}>
                          <td className="px-2 py-2.5 text-center text-gray-500 font-medium">
                            {idx + 1}
                          </td>
                          <td className={`px-3 py-2.5 font-medium text-slate-900 sticky left-0 ${rowBg || 'bg-white'} z-10`}>
                            {item.item_name}
                          </td>
                          <td className="px-3 py-2.5 text-center font-semibold">
                            {item.total_stock?.toLocaleString() || 0}
                          </td>
                          <td className="px-3 py-2.5 text-center">
                            <span className="font-semibold text-blue-600">{ado}</span>
                            <span className="text-gray-400 text-xs">/day</span>
                          </td>
                          <td className="px-3 py-2.5 text-center">
                            <span className={`text-xs font-semibold px-2 py-1 rounded ${movementColor}`}>
                              {movement}
                            </span>
                          </td>
                          <td className="px-3 py-2.5 text-center">
                            {incomingStock > 0 ? (
                              <div className="text-center">
                                <span className="font-bold text-cyan-600">{incomingStock.toLocaleString()}</span>
                                {incomingArrival && (
                                  <div className="text-xs text-gray-500">{incomingArrival}</div>
                                )}
                              </div>
                            ) : (
                              <span className="text-gray-400">-</span>
                            )}
                          </td>
                          <td className="px-3 py-2.5 text-center">
                            <span className={`font-bold ${dtlColor}`}>
                              {item.total_stock === 0 ? "0" : dtl === "∞" ? "∞" : typeof dtl === 'number' ? `${dtl} days` : dtl}
                            </span>
                          </td>
                          <td className="px-3 py-2.5 text-center">
                            <span className={`font-semibold ${item.total_stock === 0 ? 'text-red-600' : typeof dtl === 'number' && dtl <= 30 ? 'text-red-600' : 'text-slate-600'}`}>
                              {item.total_stock === 0 ? "OUT OF STOCK" : osDate || "-"}
                            </span>
                          </td>
                          <td className="px-3 py-2.5 text-center">
                            <span className={`text-xs font-bold px-2 py-1 rounded ${statusColor}`}>
                              {statusLabel}
                            </span>
                          </td>
                          {orderFilterOn && (
                            <td className="px-3 py-2.5 text-center">
                              <span className="text-base font-bold text-orange-700" data-testid={`order-qty-${idx}`}>
                                {item.suggested_qty ?? 0}
                              </span>
                            </td>
                          )}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
          
          {/* Legend */}
          <div className="flex flex-wrap gap-4 text-sm">
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 bg-red-500 rounded"></div>
              <span>Critical (DTL ≤ 30 days)</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 bg-amber-500 rounded"></div>
              <span>Medium (DTL ≤ 60 days)</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 bg-yellow-500 rounded"></div>
              <span>Low (DTL ≤ 90 days)</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 bg-green-500 rounded"></div>
              <span>Healthy (DTL &gt; 90 days)</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 bg-cyan-500 rounded"></div>
              <span>INCOMING = Has incoming stock</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 bg-orange-500 rounded"></div>
              <span>FOLLOW UP! = Incoming stock arrives late</span>
            </div>
          </div>
        </TabsContent>

        {/* Matrix Tab */}
        <TabsContent value="matrix" className="space-y-4">
          {/* Filters */}
          <Card>
            <CardContent className="p-4">
              <div className="flex flex-col md:flex-row gap-3">
                <div className="flex-1 relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    placeholder="Search items..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                  />
                </div>
                
                <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                  <SelectTrigger className="w-full md:w-40">
                    <SelectValue placeholder="Category" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Categories</SelectItem>
                    {categories.map(cat => (
                      <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="w-full md:w-44">
                    <SelectValue placeholder="Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="CRITICAL">🔴 Critical</SelectItem>
                    <SelectItem value="ORDER NOW">🟠 Order Now</SelectItem>
                    <SelectItem value="PARTIAL COVERAGE">🟡 Partial Coverage</SelectItem>
                    <SelectItem value="MONITOR">🔵 Monitor</SelectItem>
                    <SelectItem value="HEALTHY">🟢 Healthy</SelectItem>
                    <SelectItem value="NO SALES DATA">⚪ No Sales Data</SelectItem>
                    <SelectItem value="LOW DEMAND">⬜ Low Demand (No Reorder)</SelectItem>
                  </SelectContent>
                </Select>
                
                <Button
                  variant={showZero ? "default" : "outline"}
                  onClick={() => setShowZero(!showZero)}
                  size="sm"
                >
                  {showZero ? "Hide Zero" : "Show Zero"}
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Matrix Table */}
          <Card>
            <CardContent className="p-0">
              {loading ? (
                <div className="flex justify-center items-center py-20">
                  <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-gray-50">
                        <TableHead className="font-bold min-w-[200px] sticky left-0 bg-gray-50 z-10">
                          Item Name
                        </TableHead>
                        {BRANCH_CODES.map(branch => (
                          <TableHead key={branch} className="text-center min-w-[70px]">
                            <div className="flex flex-col items-center">
                              <span className="font-bold" style={{ color: BRANCHES[branch].color }}>
                                {branch}
                              </span>
                              <span className="text-[10px] text-gray-400 font-normal">
                                Stock
                              </span>
                            </div>
                          </TableHead>
                        ))}
                        <TableHead className="text-center bg-gray-100 font-bold min-w-[70px]">
                          TOTAL
                        </TableHead>
                        <TableHead className="text-center min-w-[120px]">
                          <span className="font-bold">Distribution</span>
                        </TableHead>
                        <TableHead className="text-center min-w-[60px]">
                          <div 
                            className="flex flex-col items-center cursor-help"
                            title="ADO = Average Daily Out. For low-velocity items (≤10 sales in 30d), system uses BLENDED ADO (50% × 30d + 30% × 60d + 20% × 90d) for stability."
                          >
                            <span className="font-bold text-blue-600">ADO</span>
                            <span className="text-[10px] text-gray-400 font-normal">Avg/Day</span>
                          </div>
                        </TableHead>
                        <TableHead className="text-center min-w-[80px]">
                          <div className="flex flex-col items-center">
                            <span className="font-bold text-amber-600">DTL</span>
                            <span className="text-[10px] text-gray-400 font-normal">Days Left</span>
                          </div>
                        </TableHead>
                        <TableHead className="text-center font-bold min-w-[70px]">Status</TableHead>
                        <TableHead className="text-center font-bold min-w-[90px]">Action</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredItems.map((item, idx) => (
                        <TableRow key={idx} className={`hover:bg-gray-50 ${item.needsRestock ? 'bg-orange-50/50' : ''}`}>
                          <TableCell className="font-medium sticky left-0 bg-white z-10">
                            <div className="max-w-[220px]">
                              <span className="block truncate" title={item.item_name}>
                                {item.item_name}
                              </span>
                              {/* Subtext: branch names where stock is zero or low, same tone as status badge */}
                              {(item.zero_channels || []).length > 0 && (
                                <span
                                  className={`text-[10px] block mt-0.5 ${(STATUS_STYLES[item.status] || STATUS_STYLES["PARTIAL COVERAGE"]).text}`}
                                  title={`Zero stock in: ${item.zero_channels.join(", ")}`}
                                >
                                  ⚠ Zero in {item.zero_channels.join(", ")}
                                </span>
                              )}
                              {(item.zero_channels || []).length === 0 && item.branch_stockout_count > 0 && (
                                <span
                                  className="text-[10px] block mt-0.5 text-[#CA8A04]"
                                  title={`Below threshold: ${(item.critical_branches || []).map(c => `${c.branch}(${c.stock})`).join(", ")}`}
                                >
                                  ⚠ Low in {(item.critical_branches || []).map(c => c.branch).join(", ")}
                                </span>
                              )}
                              {item.needsRestock && !(item.zero_channels || []).length && !item.branch_stockout_count && (
                                <span className="text-[10px] text-orange-600">⚠️ Low in {userBranch}</span>
                              )}
                            </div>
                          </TableCell>
                          
                          {BRANCH_CODES.map(branch => (
                            <TableCell key={branch} className={getCellStyle(item.branches[branch], branch === userBranch)}>
                              <div className="flex flex-col items-center">
                                <span>{item.branches[branch] || 0}</span>
                                {item.distribution[branch] > 0 && (
                                  <span className="text-[9px] text-gray-400">
                                    {item.distribution[branch]}%
                                  </span>
                                )}
                              </div>
                            </TableCell>
                          ))}
                          
                          <TableCell className="text-center font-bold bg-gray-50">
                            {item.total_stock}
                          </TableCell>
                          
                          <TableCell className="px-2">
                            <DistributionBar item={item} />
                          </TableCell>
                          
                          {/* ADO Column — clickable para makita saan galing ang numero */}
                          <TableCell className="text-center">
                            <button
                              onClick={() => openAdoBreakdown(item.item_name)}
                              className={`font-semibold underline decoration-dotted underline-offset-2 cursor-pointer hover:opacity-70 ${item.ado >= 5 ? 'text-green-600' : item.ado >= 1 ? 'text-blue-600' : 'text-gray-400'}`}
                              title="I-click para makita ang breakdown ng ADO"
                              data-testid={`ado-value-${item.item_name}`}
                            >
                              {item.ado || 0}
                            </button>
                          </TableCell>
                          
                          {/* DTL Column */}
                          <TableCell className="text-center">
                            <div className="flex flex-col items-center">
                              {item.dtl === null || item.dtl === undefined ? (
                                <span className="text-gray-400 font-semibold">—</span>
                              ) : typeof item.dtl === 'number' && item.dtl > 3650 ? (
                                <span className="text-gray-500 font-semibold" title={`Theoretical DTL: ${item.dtl}d (essentially no movement)`}>10y+</span>
                              ) : (
                                <>
                                  <span className={`font-semibold ${
                                    item.dtl === 0 || item.dtl === "NOW" ? 'text-red-600' :
                                    typeof item.dtl === 'number' && item.dtl <= 7 ? 'text-red-600' :
                                    typeof item.dtl === 'number' && item.dtl <= 14 ? 'text-amber-600' :
                                    typeof item.dtl === 'number' && item.dtl <= 30 ? 'text-yellow-600' :
                                    'text-green-600'
                                  }`}>
                                    {item.dtl === "∞" ? "∞" : item.dtl}
                                  </span>
                                  {item.projected_out_date && item.projected_out_date !== "-" && (
                                    <span className="text-[10px] text-gray-400">{item.projected_out_date}</span>
                                  )}
                                </>
                              )}
                            </div>
                          </TableCell>
                          
                          <TableCell className="text-center">
                            <Badge 
                              className={`${(STATUS_STYLES[item.status] || STATUS_STYLES.HEALTHY).bg} ${(STATUS_STYLES[item.status] || STATUS_STYLES.HEALTHY).text} ${(STATUS_STYLES[item.status] || STATUS_STYLES.HEALTHY).border} border text-[11px] font-semibold rounded-full px-3 py-0.5 whitespace-nowrap`}
                              title={(item.restock_triggers || []).length > 0 ? `Triggers: ${item.restock_triggers.join(", ")}` : ""}
                            >
                              {item.status}
                            </Badge>
                          </TableCell>
                          
                          <TableCell className="text-center">
                            <Button
                              size="sm"
                              variant={item.needsRestock ? "default" : "outline"}
                              onClick={() => openRequestModal(item)}
                              disabled={item.sourceBranches.length === 0}
                              className={`gap-1 text-xs ${item.needsRestock ? 'bg-orange-500 hover:bg-orange-600' : ''}`}
                            >
                              <Send className="h-3 w-3" />
                              Request
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                      
                      {filteredItems.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={10} className="text-center py-10 text-gray-500">
                            No items found matching your filters
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Distribution Analysis Tab */}
        <TabsContent value="distribution" className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Category Distribution */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-lg flex items-center gap-2">
                  <PieChart className="h-5 w-5 text-indigo-600" />
                  Stock by Category
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {categoryStats.slice(0, 10).map((cat, idx) => (
                    <div key={cat.category} className="flex items-center gap-3">
                      <span className="w-6 text-sm font-bold text-gray-400">{idx + 1}</span>
                      <div className="flex-1">
                        <div className="flex justify-between items-center mb-1">
                          <span className="font-medium">{cat.category}</span>
                          <span className="text-sm text-gray-500">{cat.totalStock.toLocaleString()} units</span>
                        </div>
                        <div className="flex h-2 rounded-full overflow-hidden bg-gray-100">
                          {BRANCH_CODES.map(branch => {
                            const pct = cat.totalStock > 0 ? (cat.branches[branch] / cat.totalStock) * 100 : 0;
                            return (
                              <div
                                key={branch}
                                style={{ width: `${pct}%`, backgroundColor: BRANCHES[branch].color }}
                                title={`${branch}: ${cat.branches[branch]}`}
                              />
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Branch Distribution Legend */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-lg flex items-center gap-2">
                  <BarChart3 className="h-5 w-5 text-indigo-600" />
                  Branch Stock Summary
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {BRANCH_CODES.map(branch => {
                    const branchTotal = filteredItems.reduce((sum, item) => sum + (item.branches[branch] || 0), 0);
                    const grandTotal = filteredItems.reduce((sum, item) => sum + item.total_stock, 0);
                    const pct = grandTotal > 0 ? Math.round((branchTotal / grandTotal) * 100) : 0;
                    
                    return (
                      <div key={branch}>
                        <div className="flex justify-between items-center mb-1">
                          <div className="flex items-center gap-2">
                            <div className="w-3 h-3 rounded-full" style={{ backgroundColor: BRANCHES[branch].color }} />
                            <span className="font-medium">{branch}</span>
                            <span className="text-xs text-gray-400">({BRANCHES[branch].name})</span>
                          </div>
                          <div className="text-right">
                            <span className="font-bold">{branchTotal.toLocaleString()}</span>
                            <span className="text-sm text-gray-500 ml-1">({pct}%)</span>
                          </div>
                        </div>
                        <div className="w-full bg-gray-100 rounded-full h-3">
                          <div
                            className="h-3 rounded-full transition-all"
                            style={{ 
                              width: `${pct}%`, 
                              backgroundColor: BRANCHES[branch].color 
                            }}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Restock Suggestions Tab */}
        <TabsContent value="restock" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <ArrowRightLeft className="h-5 w-5 text-orange-600" />
                Restock Suggestions for {userBranch}
              </CardTitle>
              <p className="text-sm text-gray-500">
                Items low in your branch that can be requested from other branches
              </p>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {filteredItems
                  .filter(item => item.needsRestock)
                  .sort((a, b) => a.branches[userBranch] - b.branches[userBranch])
                  .slice(0, 20)
                  .map((item, idx) => (
                    <div key={idx} className="flex items-center justify-between p-3 bg-orange-50 rounded-lg border border-orange-200">
                      <div className="flex-1">
                        <p className="font-medium">{item.item_name}</p>
                        <div className="flex items-center gap-4 mt-1 text-sm">
                          <span className="text-orange-600">
                            Your stock ({userBranch}): <strong>{item.branches[userBranch]}</strong>
                          </span>
                          <span className="text-green-600">
                            Best source: <strong>{item.best_source_branch}</strong> ({item.best_source_qty} available)
                          </span>
                        </div>
                      </div>
                      <Button
                        size="sm"
                        onClick={() => openRequestModal(item)}
                        className="bg-orange-500 hover:bg-orange-600 gap-1"
                      >
                        <Send className="h-3 w-3" />
                        Request
                      </Button>
                    </div>
                  ))}
                
                {filteredItems.filter(item => item.needsRestock).length === 0 && (
                  <div className="text-center py-10 text-gray-500">
                    <CheckCircle className="h-12 w-12 mx-auto mb-2 text-green-400" />
                    <p>All items are well-stocked in {userBranch}!</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Request Modal */}
      <Dialog open={showRequestModal} onOpenChange={setShowRequestModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ArrowRightLeft className="h-5 w-5 text-blue-600" />
              Request Item Transfer
            </DialogTitle>
          </DialogHeader>
          
          {selectedItem && (
            <div className="space-y-4 py-4">
              <div className="p-3 bg-gray-50 rounded-lg">
                <p className="font-bold text-gray-900">{selectedItem.item_name}</p>
                <p className="text-sm text-gray-500">Total: {selectedItem.total_stock} units across all branches</p>
              </div>
              
              {/* Visual stock distribution */}
              <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                <p className="text-sm font-medium text-blue-800 mb-2">Stock Distribution:</p>
                <div className="grid grid-cols-5 gap-2 text-center">
                  {BRANCH_CODES.map(branch => (
                    <div key={branch} className={`p-2 rounded ${branch === userBranch ? 'bg-blue-200' : 'bg-white'}`}>
                      <p className="text-xs font-bold" style={{ color: BRANCHES[branch].color }}>{branch}</p>
                      <p className={`text-lg font-bold ${selectedItem.branches[branch] > 0 ? 'text-gray-900' : 'text-gray-300'}`}>
                        {selectedItem.branches[branch]}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
              
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Request FROM
                  </label>
                  <Select value={requestSource} onValueChange={setRequestSource}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select source" />
                    </SelectTrigger>
                    <SelectContent>
                      {/* Show ALL branches with stock for admins, or filter out user's branch for regular users */}
                      {BRANCH_CODES
                        .filter(b => selectedItem.branches[b] > 0)
                        .sort((a, b) => (selectedItem.branches[b] || 0) - (selectedItem.branches[a] || 0))
                        .map(branch => (
                        <SelectItem key={branch} value={branch}>
                          <span style={{ color: BRANCHES[branch].color }}>●</span> {branch} - {BRANCHES[branch].name} ({selectedItem.branches[branch]} available)
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {/* Show suggestion for highest stock branch */}
                  {(() => {
                    const highestBranch = BRANCH_CODES
                      .filter(b => selectedItem.branches[b] > 0)
                      .sort((a, b) => (selectedItem.branches[b] || 0) - (selectedItem.branches[a] || 0))[0];
                    return highestBranch ? (
                      <p className="text-xs text-green-600 mt-1">
                        💡 Suggested: {highestBranch} has highest stock ({selectedItem.branches[highestBranch]})
                      </p>
                    ) : null;
                  })()}
                  {requestSource && requestDest && requestSource === requestDest && (
                    <p className="text-xs text-red-500 mt-1 font-medium">
                      ⚠️ Source at destination ay pareho! Piliin ibang destination branch.
                    </p>
                  )}
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Request TO (Your Branch)
                  </label>
                  <Select value={requestDest} onValueChange={setRequestDest}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select destination" />
                    </SelectTrigger>
                    <SelectContent>
                      {BRANCH_CODES.map(branch => (
                        <SelectItem key={branch} value={branch}>
                          <span style={{ color: BRANCHES[branch].color }}>●</span> {branch} - {BRANCHES[branch].name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Quantity
                  </label>
                  <Input
                    type="number"
                    min="0.5"
                    step="1"
                    value={requestQty}
                    onChange={(e) => setRequestQty(parseFloat(e.target.value) || 0)}
                    data-testid="request-qty-input"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Tip: I-type ang exactly na quantity. Pwede ang 0.5 (half box) — kailangan mong i-type, not click arrows.
                  </p>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Notes (optional)
                  </label>
                  <Input
                    placeholder="Reason for request..."
                    value={requestNotes}
                    onChange={(e) => setRequestNotes(e.target.value)}
                  />
                </div>
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRequestModal(false)}>Cancel</Button>
            <Button 
              onClick={submitRequest}
              disabled={submitting || !requestSource || !requestDest || requestQty <= 0 || requestSource === requestDest}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Send className="h-4 w-4 mr-2" />}
              {submitting ? "Sending..." : "Send Request"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Order All Modal — consolidated purchase order for CRITICAL + ORDER NOW items */}
      <Dialog open={showOrderAllModal} onOpenChange={setShowOrderAllModal}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto" data-testid="order-all-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-[#DC2626]">
              <XCircle className="h-5 w-5" />
              Consolidated Purchase Order — Critical + Order Now Items
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-gray-600">
              Suggested reorder qty computed for <strong>60-day target coverage</strong>:
              <code className="text-xs bg-gray-100 px-1 rounded ml-1">max(0, ceil(ADO × 60 − current_stock − incoming))</code>
            </p>
            <div className="flex justify-between items-center">
              <span className="text-sm font-medium">{orderAllSuggestions.length} items to order</span>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    const text = orderAllSuggestions
                      .map((r) => `${r.item_name} — Qty: ${r.suggested_qty} (${r.status}, DTL ${r.dtl}d)`)
                      .join("\n");
                    copyToClipboard(text, "Order list");
                  }}
                  data-testid="copy-order-list-btn"
                >
                  📋 Copy
                </Button>
                <Button
                  size="sm"
                  onClick={() => downloadCSV(orderAllSuggestions, `critical-order-${new Date().toISOString().slice(0, 10)}.csv`)}
                  className="bg-[#DC2626] hover:bg-[#B91C1C]"
                  data-testid="export-order-csv-btn"
                >
                  📥 Export CSV
                </Button>
              </div>
            </div>
            <div className="overflow-x-auto border rounded-lg">
              <table className="w-full text-sm">
                <thead className="bg-red-50 text-xs uppercase text-[#DC2626]">
                  <tr>
                    <th className="px-3 py-2 text-left">Item</th>
                    <th className="px-3 py-2 text-center">Status</th>
                    <th className="px-3 py-2 text-right">Stock</th>
                    <th className="px-3 py-2 text-right">Incoming</th>
                    <th className="px-3 py-2 text-right">ADO</th>
                    <th className="px-3 py-2 text-right">DTL</th>
                    <th className="px-3 py-2 text-right font-bold">Order Qty</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {orderAllSuggestions.length === 0 ? (
                    <tr><td colSpan={7} className="text-center py-8 text-gray-400">🎉 Walang critical items — lahat healthy!</td></tr>
                  ) : orderAllSuggestions.map((r, idx) => (
                    <tr key={idx} className="hover:bg-red-50/40">
                      <td className="px-3 py-2 font-medium">{r.item_name}</td>
                      <td className="px-3 py-2 text-center">
                        <Badge className={`${STATUS_STYLES[r.status]?.bg} ${STATUS_STYLES[r.status]?.text} text-[10px]`}>{r.status}</Badge>
                      </td>
                      <td className="px-3 py-2 text-right font-mono">{r.current_stock}</td>
                      <td className="px-3 py-2 text-right font-mono text-gray-500">{r.incoming}</td>
                      <td className="px-3 py-2 text-right font-mono">{r.ado}</td>
                      <td className="px-3 py-2 text-right font-mono">{r.dtl ?? "—"}</td>
                      <td className="px-3 py-2 text-right font-bold text-[#DC2626]">{r.suggested_qty}</td>
                    </tr>
                  ))}
                </tbody>
                {orderAllSuggestions.length > 0 && (
                  <tfoot className="bg-gray-50 font-bold">
                    <tr>
                      <td colSpan={6} className="px-3 py-2 text-right">Total Units to Order:</td>
                      <td className="px-3 py-2 text-right text-[#DC2626]">
                        {orderAllSuggestions.reduce((s, r) => s + r.suggested_qty, 0).toLocaleString()}
                      </td>
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowOrderAllModal(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Transfer Suggestions Modal — rebalance PARTIAL COVERAGE items */}
      <Dialog open={showTransferModal} onOpenChange={setShowTransferModal}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto" data-testid="transfer-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-[#CA8A04]">
              <AlertTriangle className="h-5 w-5" />
              Transfer Suggestions — Rebalance Partial Coverage Items
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-gray-600">
              Auto-computed transfers from highest-stock branch to zero-stock branches (10% share per destination).
            </p>
            <div className="flex justify-between items-center">
              <span className="text-sm font-medium">{transferSuggestions.length} transfer suggestions</span>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    const text = transferSuggestions
                      .map((r) => `${r.item_name}: ${r.from_branch}(${r.from_stock}) → ${r.to_branch} (qty ${r.suggested_qty})`)
                      .join("\n");
                    copyToClipboard(text, "Transfer suggestions");
                  }}
                  data-testid="copy-transfer-list-btn"
                >
                  📋 Copy
                </Button>
                <Button
                  size="sm"
                  onClick={() => downloadCSV(transferSuggestions, `transfer-suggestions-${new Date().toISOString().slice(0, 10)}.csv`)}
                  className="bg-[#CA8A04] hover:bg-[#A16207]"
                  data-testid="export-transfer-csv-btn"
                >
                  📥 Export CSV
                </Button>
              </div>
            </div>
            <div className="overflow-x-auto border rounded-lg">
              <table className="w-full text-sm">
                <thead className="bg-yellow-50 text-xs uppercase text-[#CA8A04]">
                  <tr>
                    <th className="px-3 py-2 text-left">Item</th>
                    <th className="px-3 py-2 text-center">From (Source)</th>
                    <th className="px-3 py-2 text-right">Source Stock</th>
                    <th className="px-3 py-2 text-center">→</th>
                    <th className="px-3 py-2 text-center">To (Destination)</th>
                    <th className="px-3 py-2 text-right font-bold">Transfer Qty</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {transferSuggestions.length === 0 ? (
                    <tr><td colSpan={6} className="text-center py-8 text-gray-400">Walang partial coverage items — well-distributed!</td></tr>
                  ) : transferSuggestions.map((r, idx) => (
                    <tr key={idx} className="hover:bg-yellow-50/40">
                      <td className="px-3 py-2 font-medium">{r.item_name}</td>
                      <td className="px-3 py-2 text-center font-semibold text-blue-700">{r.from_branch}</td>
                      <td className="px-3 py-2 text-right font-mono text-gray-500">{r.from_stock}</td>
                      <td className="px-3 py-2 text-center text-gray-400">→</td>
                      <td className="px-3 py-2 text-center font-semibold text-purple-700">{r.to_branch}</td>
                      <td className="px-3 py-2 text-right font-bold text-[#CA8A04]">{r.suggested_qty}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-xs text-gray-500 pt-2 border-t">
              💡 Tip: I-export mo yung CSV at ipasa sa Ops team para ma-execute ang transfers sa Inventory → Transfer flow.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowTransferModal(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ADO BREAKDOWN DIALOG */}
      <Dialog open={!!adoBreakdown} onOpenChange={(v) => !v && setAdoBreakdown(null)}>
        <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto" data-testid="ado-breakdown-modal">
          <DialogHeader>
            <DialogTitle>ADO Breakdown — {adoBreakdown?.item_name}</DialogTitle>
            <DialogDescription className="sr-only">Breakdown ng Average Daily Out per source</DialogDescription>
          </DialogHeader>
          {adoBreakdownLoading ? (
            <p className="text-center text-gray-500 py-6">Loading...</p>
          ) : adoBreakdown && (
            <div className="space-y-3 py-2">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-center">
                <p className="text-2xl font-bold text-blue-700" data-testid="ado-breakdown-value">{adoBreakdown.ado}</p>
                <p className="text-xs text-gray-600">ADO = {adoBreakdown.total_out} units NABENTA ÷ {adoBreakdown.period_days} days</p>
              </div>
              <div className="space-y-1.5">
                <p className="text-[11px] font-bold text-green-700 uppercase">✓ Kasama sa ADO (benta)</p>
                {(adoBreakdown.sources || []).filter((s) => s.counted !== false).map((s) => (
                  <div key={s.source} className={`flex items-center justify-between border rounded px-3 py-2 ${s.qty > 0 ? "bg-white" : "bg-gray-50 text-gray-400"}`}>
                    <span className="text-sm">{s.source} <span className="text-xs text-gray-400">({s.entries} entries)</span></span>
                    <span className="font-bold text-sm">{s.qty}</span>
                  </div>
                ))}
                <p className="text-[11px] font-bold text-gray-500 uppercase pt-1">✕ Hindi kasama (hindi benta)</p>
                {(adoBreakdown.sources || []).filter((s) => s.counted === false).map((s) => (
                  <div key={s.source} className="flex items-center justify-between border border-dashed rounded px-3 py-2 bg-gray-50 text-gray-400 opacity-75">
                    <span className="text-sm line-through decoration-gray-300">{s.source} <span className="text-xs">({s.entries} entries)</span></span>
                    <span className="text-xs font-semibold">{s.qty} <span className="font-normal">— excluded</span></span>
                  </div>
                ))}
              </div>
              {/* Purchase-Req style: OFFLINE per branch + ONLINE per channel */}
              {((adoBreakdown.offline_per_branch || []).length > 0 || (adoBreakdown.online_per_channel || []).length > 0) && (
                <div className="grid grid-cols-2 gap-2">
                  <div className="border rounded-lg p-2">
                    <p className="text-xs font-bold text-gray-600 mb-1">OFFLINE (per branch)</p>
                    {(adoBreakdown.offline_per_branch || []).length === 0 ? (
                      <p className="text-xs text-gray-400">Wala</p>
                    ) : adoBreakdown.offline_per_branch.map((b) => (
                      <div key={b.branch} className="flex justify-between text-xs py-0.5">
                        <span>{b.branch}</span>
                        <span className="font-semibold">{b.ado}/day <span className="text-gray-400">({b.qty})</span></span>
                      </div>
                    ))}
                  </div>
                  <div className="border rounded-lg p-2">
                    <p className="text-xs font-bold text-gray-600 mb-1">ONLINE (per channel)</p>
                    {(adoBreakdown.online_per_channel || []).length === 0 ? (
                      <p className="text-xs text-gray-400">Wala</p>
                    ) : adoBreakdown.online_per_channel.map((c) => (
                      <div key={c.channel} className="flex justify-between text-xs py-0.5">
                        <span>{c.channel}</span>
                        <span className="font-semibold">{c.ado}/day <span className="text-gray-400">({c.qty})</span></span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {(adoBreakdown.recent_recount_events || []).length > 0 && (
                <div>
                  <p className="text-xs font-semibold text-gray-600 mb-1">Recent recount adjustments (HINDI kasama sa ADO — stock update lang):</p>
                  <div className="space-y-1 max-h-32 overflow-y-auto">
                    {adoBreakdown.recent_recount_events.map((ev, i) => (
                      <p key={i} className="text-xs text-gray-500 border-l-2 border-amber-300 pl-2">
                        {ev.timestamp?.slice(0, 10)} • {ev.branch} • {ev.quantity} {ev.encoder ? `• ${ev.encoder}` : ""} {ev.remarks ? `• "${ev.remarks}"` : ""}
                      </p>
                    ))}
                  </div>
                </div>
              )}
              <p className="text-[11px] text-gray-400">ADO = benta lang (Walk-In + Online + Daily Out), by sale date. HINDI kasama: recounts, warranty, transfers, at internal moves.</p>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setAdoBreakdown(null)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* RECOUNT-IN-SALES CLEANUP DIALOG (admin approval flow) */}
      <Dialog open={cleanupOpen} onOpenChange={(v) => !v && setCleanupOpen(false)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="recount-cleanup-modal">
          <DialogHeader>
            <DialogTitle>Recount Cleanup — Sales Flow Scan</DialogTitle>
            <DialogDescription className="sr-only">Listahan ng recount/adjustment entries na napasok sa sales flow, para sa admin approval</DialogDescription>
          </DialogHeader>
          {cleanupLoading ? (
            <p className="text-center text-gray-500 py-6">Scanning sales entries...</p>
          ) : cleanupData && (
            <div className="space-y-3 py-2">
              <div className={`border rounded-lg p-3 text-center ${cleanupData.total_found > 0 ? "bg-rose-50 border-rose-200" : "bg-green-50 border-green-200"}`}>
                <p className="text-2xl font-bold" data-testid="cleanup-total-found">{cleanupData.total_found}</p>
                <p className="text-xs text-gray-600">
                  {cleanupData.total_found > 0
                    ? `sales entries na mukhang recount/adjustment (${cleanupData.total_qty} units) — i-review bago i-apply`
                    : "Malinis! Walang recount/adjustment entries sa sales flow."}
                </p>
              </div>
              {(cleanupData.per_branch || []).length > 0 && (
                <div className="border rounded-lg p-2">
                  <p className="text-xs font-bold text-gray-600 mb-1">Per branch:</p>
                  {cleanupData.per_branch.map((b) => (
                    <div key={b.branch} className="flex justify-between text-xs py-0.5">
                      <span>{b.branch}</span>
                      <span className="font-semibold">{b.count} entries ({b.qty} units)</span>
                    </div>
                  ))}
                </div>
              )}
              {(cleanupData.entries || []).length > 0 && (
                <div className="space-y-1 max-h-64 overflow-y-auto">
                  {cleanupData.entries.map((e, i) => (
                    <div key={`${e.collection}-${e.id}`} className="text-xs border rounded px-2 py-1.5 bg-white" data-testid={`cleanup-entry-${i}`}>
                      <div className="flex justify-between font-semibold">
                        <span>{e.date} • {e.branch} • {e.item_name}</span>
                        <span className="text-rose-600">qty {e.quantity}</span>
                      </div>
                      <p className="text-gray-500">{e.collection} • matched sa "{e.matched_field}": <span className="italic">"{e.matched_text}"</span></p>
                    </div>
                  ))}
                </div>
              )}
              <p className="text-[11px] text-gray-400">
                Ang i-aapply ay magma-mark sa entries bilang RECOUNT_ADJUSTMENT (excluded sa ADO/sales analytics). HINDI magbabago ang stock — analytics classification lang.
              </p>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setCleanupOpen(false)}>Close</Button>
            {cleanupData?.total_found > 0 && (
              <Button
                onClick={applyRecountCleanup}
                disabled={cleanupApplying}
                className="bg-rose-600 hover:bg-rose-700 text-white"
                data-testid="recount-cleanup-apply-btn"
              >
                {cleanupApplying ? "Applying..." : `Approve & Reclassify (${cleanupData.total_found})`}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
